import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CUSTOMER_TIERS,
  getSubscription,
  isCustomerTier,
  openBillingPortal,
  startCheckout,
  type CheckoutProduct,
  type ProductInfo,
  type SubscriptionView,
} from "../api/accountApi";
import { accountErrorMessage } from "../api/accountErrors";
import { LotseAddon } from "../components/Lotse";
import { Loading, Notice, Panel, Row } from "../components/Portal";
import { IconBank, IconBriefcase, IconBuilding, IconCheck } from "../components/icons";
import { useAccountAuth } from "../contexts/AccountAuthContext";
import { limitLine, productReady } from "../lib/accountFormat";
import { includedPoints, LOTSE_PRICE } from "../lib/portalCopy";

type LoadState = "loading" | "ready" | "error";

const TIER_NAMES: Record<string, string> = {
  customer: "Basis",
  customer_plus: "Plus",
  customer_premium: "Premium",
};

const ADVISOR_FLOW = [
  { title: "Freigabe", detail: "Ihre Mandanten geben ihre Daten gezielt für Sie frei." },
  { title: "Berater-Desktop", detail: "Freigegebene Mandate liegen in Ihrer eigenen App für Windows und Mac." },
  { title: "Planung", detail: "Sie planen direkt auf den freigegebenen Daten." },
];

function TierCard({
  product,
  current,
  action,
}: {
  product: ProductInfo;
  current: boolean;
  action: React.ReactNode;
}) {
  const slug = product.code === "customer" ? "basis" : product.code.replace("customer_", "");
  const limits = product.limits;
  return (
    <article className={`tier tier--${slug}${current ? " is-current" : ""}`}>
      <header className="tier__head">
        <h3>{TIER_NAMES[product.code] ?? product.label}</h3>
        {current ? <span className="tier__badge">Aktiv</span> : null}
      </header>
      {product.price_copy ? <p className="tier__price data">{product.price_copy}</p> : null}
      <ul className="tier__limits">
        <li>
          <IconBuilding />
          {limitLine(limits?.properties, "Immobilie", "Immobilien")}
        </li>
        <li>
          <IconBank />
          {limitLine(limits?.bank_accounts, "Bankkonto", "Bankkonten")}
        </li>
      </ul>
      <ul className="tier__features">
        {includedPoints("customer").map((point) => (
          <li key={point}>
            <IconCheck />
            {point}
          </li>
        ))}
      </ul>
      <div className="tier__action">{action}</div>
    </article>
  );
}

export function BillingPage() {
  const { accessToken } = useAccountAuth();
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState<SubscriptionView | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const autoStarted = useRef(false);

  const refresh = useCallback(async () => {
    setLoadState("loading");
    setError(null);
    try {
      setSub(await getSubscription(accessToken));
      setLoadState("ready");
    } catch (e) {
      setSub(null);
      setError(accountErrorMessage(e));
      setLoadState("error");
    }
  }, [accessToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const checkout = useCallback(
    async (product: CheckoutProduct) => {
      setBusy(true);
      setActionError(null);
      try {
        const { url } = await startCheckout(accessToken, product);
        window.location.href = url;
      } catch (e) {
        setActionError(accountErrorMessage(e));
        setBusy(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    if (autoStarted.current || !sub || busy || loadState !== "ready") return;
    const product = params.get("product");
    if (!isCustomerTier(product) && product !== "advisor" && product !== "ai_lotse") return;
    if (product === "ai_lotse" ? !sub.entitled || sub.ai_lotse.entitled : sub.entitled) return;
    if (!sub.stripe_configured) return;
    autoStarted.current = true;
    setParams({}, { replace: true });
    void checkout(product);
  }, [sub, params, busy, checkout, setParams, loadState]);

  const portal = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const { url } = await openBillingPortal(accessToken);
      window.location.href = url;
    } catch (e) {
      setActionError(accountErrorMessage(e));
      setBusy(false);
    }
  };

  if (loadState === "loading") return <Loading label="Mitgliedschaft wird geladen" />;

  if (loadState === "error" || !sub) {
    return (
      <Panel>
        <Row
          title="Status nicht verfügbar"
          detail={error}
          action={<button type="button" className="btn-secondary" onClick={() => void refresh()}>Erneut versuchen</button>}
        />
      </Panel>
    );
  }

  const tiers = CUSTOMER_TIERS.map((code) => sub.products.find((product) => product.code === code)).filter(
    (product): product is ProductInfo => Boolean(product),
  );
  const advisor = sub.products.find((product) => product.code === "advisor");
  const lotse = sub.products.find((product) => product.code === "ai_lotse");
  const isAdvisor = sub.entitled && sub.product_code === "advisor";
  const currentTier = sub.entitled && isCustomerTier(sub.product_code) ? sub.product_code : null;

  const tierAction = (product: ProductInfo) => {
    const name = TIER_NAMES[product.code] ?? product.label;
    const onDark = product.code === "customer_premium";
    if (product.code === currentTier) {
      return (
        <button type="button" className={onDark ? "btn-ghost-light" : "btn-secondary"} disabled={busy || !sub.stripe_configured} onClick={() => void portal()}>
          Abrechnung verwalten
        </button>
      );
    }
    if (isAdvisor) return null;
    if (currentTier) {
      return (
        <button type="button" className={onDark ? "btn-light" : "btn-secondary"} disabled={busy || !sub.stripe_configured} onClick={() => void portal()}>
          Zu {name} wechseln
        </button>
      );
    }
    const ready = productReady(sub, product.code);
    return (
      <button
        type="button"
        className={onDark ? "btn-light" : "btn-primary"}
        disabled={busy || !ready}
        onClick={() => void checkout(product.code as CheckoutProduct)}
      >
        {ready ? `${name} wählen` : "Demnächst verfügbar"}
      </button>
    );
  };

  return (
    <div className="page">
      {actionError ? <Notice tone="error">{actionError}</Notice> : null}

      {tiers.length > 0 ? (
        <div className="offer">
          <div className="tiers">
            {tiers.map((product) => (
              <TierCard key={product.code} product={product} current={product.code === currentTier} action={tierAction(product)} />
            ))}
          </div>
          <LotseAddon
            sub={sub}
            priceCopy={lotse?.price_copy || LOTSE_PRICE}
            action={
              <button
                type="button"
                className="btn-ai"
                disabled={
                  busy ||
                  !sub.entitled ||
                  (sub.ai_lotse.entitled ? !sub.stripe_configured : !productReady(sub, "ai_lotse"))
                }
                onClick={sub.ai_lotse.entitled ? () => void portal() : () => void checkout("ai_lotse")}
              >
                {!sub.entitled
                  ? "Mit Mitgliedschaft buchbar"
                  : sub.ai_lotse.entitled
                    ? "Verwalten"
                    : productReady(sub, "ai_lotse") ? "Hinzubuchen" : "Demnächst verfügbar"}
              </button>
            }
          />
        </div>
      ) : null}

      {advisor ? (
        <article className={isAdvisor ? "advisor is-current" : "advisor"} aria-label={advisor.label}>
          <div className="advisor__intro">
            <span className="advisor__mark" aria-hidden="true">
              <IconBriefcase />
            </span>
            <p className="advisor__for">Für Beraterinnen und Berater</p>
            <h3>
              {advisor.label}
              {isAdvisor ? <span className="tier__badge">Aktiv</span> : null}
            </h3>
            {advisor.price_copy ? <p className="advisor__price data">{advisor.price_copy}</p> : null}
            <button
              type="button"
              className="btn-light"
              disabled={busy || (isAdvisor ? !sub.stripe_configured : !productReady(sub, "advisor"))}
              onClick={isAdvisor ? () => void portal() : () => void checkout("advisor")}
            >
              {isAdvisor
                ? "Abrechnung verwalten"
                : productReady(sub, "advisor")
                  ? sub.entitled ? "Zu Advisor wechseln" : "Advisor wählen"
                  : "Demnächst verfügbar"}
            </button>
          </div>
          <ol className="advisor__flow">
            {ADVISOR_FLOW.map((step, index) => (
              <li key={step.title}>
                <span className="advisor__step data" aria-hidden="true">{index + 1}</span>
                <strong>{step.title}</strong>
                <p>{step.detail}</p>
              </li>
            ))}
          </ol>
        </article>
      ) : null}
    </div>
  );
}

export function BillingSuccessPage() {
  return (
    <Panel>
      <Row
        title="Zahlung erfolgreich"
        detail="Sobald die Bestätigung vorliegt, ist Ihr Konto freigeschaltet. Das kann wenige Sekunden dauern."
        action={
          <>
            <Link to="/" className="btn-primary">Zur Übersicht</Link>
            <Link to="/downloads" className="btn-secondary">Downloads</Link>
          </>
        }
      />
    </Panel>
  );
}

export function BillingCancelPage() {
  return (
    <Panel>
      <Row
        title="Checkout abgebrochen"
        detail="Es wurde nichts berechnet. Sie können den Vorgang jederzeit erneut starten."
        action={<Link to="/billing" className="btn-primary">Zurück zur Mitgliedschaft</Link>}
      />
    </Panel>
  );
}
